<template>
    <div class="ppv2-space-y-2" :class="{ isNotrequired: !required }">
        <!-- Label -->
        <BaseLabel :customSizeLabel="customSizeLabel" v-if="label" :label="label" :for-id="id" :required="required" />

        <div class="ppv2-relative">
            <!-- Hidden native date input (Overlay) -->
            <input
                type="date"
                ref="dateInput"
                :value="isoDate"
                :min="formattedMinDate"
                :max="formattedMaxDate"
                :disabled="disabled"
                class="ppv2-absolute ppv2-inset-0 ppv2-w-full ppv2-h-full ppv2-opacity-0 ppv2-cursor-pointer ppv2-z-10"
                @change="onDateChange"
                :id="id + '-native'"
                style="color-scheme: light dark"
            />

            <!-- Visible text input -->
            <input
                :id="id"
                type="text"
                :value="formattedValue"
                :placeholder="placeholder"
                :class="[
                    'ppv2-text-[#A0AEC0] ppv2-w-full ppv2-border ppv2-border-solid ppv2-border-[#9c9af880] ppv2-rounded-xl ppv2-py-2 ppv2-px-4 ppv2-pr-10 focus:ppv2-outline-none focus:ppv2-border-2',
                    inputClass,
                ]"
                :disabled="disabled"
                readonly
            />

            <!-- Calendar icon -->
            <div class="ppv2-absolute ppv2-right-3 ppv2-top-2 ppv2-cursor-pointer" @click="showDatePicker">
                <Calendar1 :size="19" color="#94A3B8" :strokeWidth="1" />
            </div>
        </div>
    </div>
</template>

<script>
import BaseLabel from './BaseLabel.vue'
import { Calendar1 } from 'lucide-vue'

export default {
    name: 'DateInput',
    components: {
        BaseLabel,
        Calendar1,
    },
    props: {
        id: {
            type: String,
            default: () => `date-input-${Math.random().toString(36).substr(2, 9)}`,
        },
        value: {
            type: [String, Date],
            default: '',
        },
        displayFormat: {
            type: String,
            default: '',
        },
        minDate: {
            type: [String, Date],
            default: null,
        },
        maxDate: {
            type: [String, Date],
            default: null,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        required: {
            type: Boolean,
            default: false,
        },
        placeholder: {
            type: String,
            default() {
                return this.$t('resource_planning.weekRangeScheduling.period_date_placeholder')
            },
        },
        label: {
            type: String,
            default: '',
        },
        inputClass: {
            type: String,
            default: '',
        },
        customSizeLabel: {
            type: Number,
            default: 15,
        },
    },
    computed: {
        formattedMinDate() {
            if (!this.minDate) return ''
            const date = typeof this.minDate === 'string' ? new Date(this.minDate) : this.minDate
            if (isNaN(date.getTime())) return ''
            return date.toISOString().split('T')[0]
        },
        formattedMaxDate() {
            if (!this.maxDate) return ''
            const date = typeof this.maxDate === 'string' ? new Date(this.maxDate) : this.maxDate
            if (isNaN(date.getTime())) return ''
            return date.toISOString().split('T')[0]
        },
        isoDate() {
            if (!this.value) return ''
            const date = typeof this.value === 'string' ? new Date(this.value) : this.value
            if (isNaN(date.getTime())) return ''
            return date.toISOString().split('T')[0]
        },
        formattedValue() {
            if (!this.value) return ''
            const date = typeof this.value === 'string' ? new Date(this.value) : this.value
            if (isNaN(date.getTime())) return ''
            const options = {
                weekday: 'long',
                day: 'numeric',
                month: 'long',
                year: 'numeric',
            }
            const day = String(date.getDate()).padStart(2, '0')
            const month = String(date.getMonth() + 1).padStart(2, '0')
            const year = String(date.getFullYear())
            const formatted = date.toLocaleDateString('fr-FR', options)
            if (this.displayFormat) {
                return formatted.charAt(0).toUpperCase() + formatted.slice(1)
            } else {
                return `${day}/${month}/${year}`
            }
        },
    },
    methods: {
        showDatePicker() {
            if (this.disabled) return
            try {
                if (this.$refs.dateInput.showPicker) {
                    this.$refs.dateInput.showPicker()
                } else {
                    this.$refs.dateInput.click()
                }
            } catch (e) {
                this.$refs.dateInput.click()
            }
        },

        onDateChange(event) {
            // Emit the date string directly in YYYY-MM-DD format
            this.$emit('input', event.target.value || '')
        },

        onBlur() {
            this.$emit('blur')
        },

        formatInput(value) {
            // Remove all non-digit characters
            let digits = value.replace(/\D/g, '')

            // Add slashes automatically
            if (digits.length > 4) {
                digits = `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4, 8)}`
            } else if (digits.length > 2) {
                digits = `${digits.slice(0, 2)}/${digits.slice(2)}`
            }

            return digits.slice(0, 10) // Limit to dd/mm/yyyy format
        },

        parseDate(value) {
            if (!value) return null

            const [day, month, year] = value.split('/').map(Number)
            if (!day || !month || !year) return null

            const date = new Date(year, month - 1, day)
            return isNaN(date.getTime()) ? null : date
        },

        onInput(value) {
            const formatted = this.formatInput(value)
            const date = this.parseDate(formatted)

            if (date) {
                // Format date as YYYY-MM-DD
                const formattedDate = this.formatDateForApi(date)

                // Validate against min and max dates if provided
                if (this.minDate) {
                    const minDate = new Date(this.minDate)
                    if (date < minDate) {
                        this.$emit('input', this.formatDateForApi(minDate))
                        return
                    }
                }

                if (this.maxDate) {
                    const maxDate = new Date(this.maxDate)
                    if (date > maxDate) {
                        this.$emit('input', this.formatDateForApi(maxDate))
                        return
                    }
                }

                this.$emit('input', formattedDate)
            } else {
                this.$emit('input', formatted)
            }
        },

        onBlur() {
            const date = this.parseDate(this.formattedValue)
            if (date) {
                this.$emit('input', this.formatDateForApi(date))
            }
            this.$emit('blur')
        },

        formatDateForApi(date) {
            if (!date) return ''
            const d = new Date(date)
            if (isNaN(d.getTime())) return ''

            const year = d.getFullYear()
            const month = String(d.getMonth() + 1).padStart(2, '0')
            const day = String(d.getDate()).padStart(2, '0')

            return `${year}-${month}-${day}`
        },

        focus() {
            this.$refs.input.focus()
        },
    },
}
</script>
